<!--
  @component CollectionModal
  @description Modal for creating and renaming collections
-->
<script lang="ts">
  import { onDestroy } from 'svelte';
  import { t } from '@/i18n';
  import { collectionModal, hideCollectionModal, setCollectionModalName } from '@stores/uiStore';
  import { selectedLibraryId, selectedLibraryType } from '@stores/libraryStore';
  import { addCollection, updateCollection } from '@stores/collectionStore';
  import { collectionActions } from '@services/collectionActionsService';
  import { trapFocus, createModalKeyboardHandler } from '@/utils';

  let inputElement: HTMLInputElement;
  let modalElement: HTMLDivElement;
  let isLoading = false;
  let releaseFocusTrap: (() => void) | null = null;

  // Activate focus trap when modal opens (input focus handled separately)
  $: if ($collectionModal.visible && modalElement) {
    releaseFocusTrap = trapFocus(modalElement, { autoFocus: false });
  }

  // Clean up focus trap on destroy
  onDestroy(() => {
    releaseFocusTrap?.();
  });

  // Modal title based on mode
  $: modalTitle = getModalTitle($collectionModal.mode);

  function getModalTitle(mode: string): string {
    switch (mode) {
      case 'create':
        return $t('collection.newCollection');
      case 'create-sub':
        return $t('collection.newSubcollection');
      case 'rename':
        return $t('collection.renameCollection');
      default:
        return $t('collection.title');
    }
  }

  // Placeholder based on mode
  $: placeholder = getPlaceholder($collectionModal.mode);

  function getPlaceholder(mode: string): string {
    switch (mode) {
      case 'rename':
        return $t('collection.collectionNamePlaceholder');
      default:
        return $t('collection.collectionNamePlaceholder');
    }
  }

  // Handle submit
  async function handleSubmit() {
    const name = $collectionModal.collectionName.trim();
    if (!name || !$selectedLibraryId || !$selectedLibraryType) return;

    isLoading = true;

    try {
      if ($collectionModal.mode === 'rename' && $collectionModal.editingCollection) {
        // Rename existing collection
        const result = await collectionActions.renameCollection(
          $selectedLibraryId,
          $selectedLibraryType,
          $collectionModal.editingCollection,
          name
        );

        if (result.success) {
          updateCollection($collectionModal.editingCollection.key, result.data);
          hideCollectionModal();
        }
      } else {
        // Create new collection
        const parentKey = $collectionModal.parentCollection?.key;
        const result = await collectionActions.createCollection(
          $selectedLibraryId,
          $selectedLibraryType,
          name,
          parentKey
        );

        if (result.success) {
          addCollection(result.data);
          hideCollectionModal();
        }
      }
    } finally {
      isLoading = false;
    }
  }

  // Handle cancel
  function handleCancel() {
    releaseFocusTrap?.();
    releaseFocusTrap = null;
    hideCollectionModal();
  }

  // Centralized keyboard handling
  $: keyboard = createModalKeyboardHandler({
    onEscape: handleCancel,
    onEnter: handleSubmit,
    enterCondition: () => $collectionModal.collectionName.trim() !== '',
  });

  // Handle input change
  function handleInput(event: Event) {
    const target = event.target as HTMLInputElement;
    setCollectionModalName(target.value);
  }

  // Focus input when modal opens
  $: if ($collectionModal.visible && inputElement) {
    setTimeout(() => {
      inputElement.focus();
      inputElement.select();
    }, 100);
  }
</script>

{#if $collectionModal.visible}
  <div class="modal-overlay" on:keydown={keyboard.handleKeydown} role="presentation">
    <button
      type="button"
      class="modal-backdrop"
      on:click={handleCancel}
      aria-label={$t('common.close')}
      tabindex="-1"
    ></button>
    <div
      bind:this={modalElement}
      class="modal-content"
      role="dialog"
      aria-modal="true"
      aria-labelledby="collection-modal-title"
    >
      <div class="modal-header">
        <h3 id="collection-modal-title" class="modal-title">{modalTitle}</h3>
        <button class="close-button" on:click={handleCancel} aria-label={$t('common.close')}>
          ×
        </button>
      </div>

      <div class="modal-body">
        {#if $collectionModal.parentCollection}
          <div class="parent-info">
            <span class="parent-label">{$t('detailsPanel.info')}:</span>
            <span class="parent-name">📁 {$collectionModal.parentCollection.data.name}</span>
          </div>
        {/if}

        <div class="input-group">
          <label class="input-label" for="collection-name">
            {$t('collection.collectionName')}
          </label>
          <input
            id="collection-name"
            bind:this={inputElement}
            class="collection-input"
            {placeholder}
            value={$collectionModal.collectionName}
            on:input={handleInput}
            on:keydown={keyboard.handleKeydown}
            disabled={isLoading}
          />
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-secondary" on:click={handleCancel} disabled={isLoading}>
          {$t('common.cancel')}
        </button>
        <button
          class="btn btn-primary"
          on:click={handleSubmit}
          disabled={!$collectionModal.collectionName.trim() || isLoading}
        >
          {#if isLoading}
            <span class="loading-spinner"></span>
          {:else}
            {$collectionModal.mode === 'rename'
              ? $t('collection.renameCollection')
              : $t('common.create')}
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2500;
  }

  .modal-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    border: none;
    cursor: default;
    animation: fadeIn 0.2s ease-out;
  }

  .modal-content {
    position: relative;
    background: var(--color-bg-secondary, #383838);
    border-radius: 8px;
    width: 450px;
    max-width: 90vw;
    border: 1px solid var(--color-border, #555);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    animation: slideIn 0.2s ease-out;
  }

  .modal-header {
    padding: 20px 24px 16px;
    border-bottom: 1px solid var(--color-border, #555);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .modal-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-primary, #e8e8e8);
  }

  .close-button {
    background: none;
    border: none;
    color: var(--color-text-secondary, #aaa);
    font-size: 24px;
    cursor: pointer;
    padding: 0;
    line-height: 1;
  }

  .close-button:hover {
    color: var(--color-text-primary, #e8e8e8);
  }

  .modal-body {
    padding: 20px 24px;
  }

  .parent-info {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    padding: 12px;
    background: var(--color-bg-primary, #2b2b2b);
    border-radius: 6px;
    border: 1px solid var(--color-border-light, #444);
  }

  .parent-label {
    font-size: 12px;
    color: var(--color-text-muted, #aaa);
    font-weight: 500;
  }

  .parent-name {
    font-size: 13px;
    color: var(--color-text-primary, #e8e8e8);
    font-weight: 500;
  }

  .input-group {
    margin-bottom: 0;
  }

  .input-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-secondary, #bbb);
    margin-bottom: 6px;
  }

  .collection-input {
    width: 100%;
    padding: 10px 12px;
    background: var(--color-bg-primary, #2b2b2b);
    border: 1px solid var(--color-border, #555);
    border-radius: 6px;
    color: var(--color-text-primary, #e8e8e8);
    font-size: 14px;
    box-sizing: border-box;
    transition:
      border-color 0.2s,
      box-shadow 0.2s;
  }

  .collection-input:focus {
    outline: none;
    border-color: var(--color-accent, #5c7cfa);
    box-shadow: 0 0 0 3px rgba(92, 124, 250, 0.1);
  }

  .collection-input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .collection-input::placeholder {
    color: var(--color-text-muted, #888);
  }

  .modal-footer {
    padding: 16px 24px 20px;
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    border-top: 1px solid var(--color-border, #555);
  }

  .btn {
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .btn-primary {
    background: var(--color-accent, #5c7cfa);
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    background: var(--color-accent-hover, #4c6ef5);
    transform: translateY(-1px);
  }

  .btn-secondary {
    background: var(--color-bg-tertiary, #666);
    color: white;
  }

  .btn-secondary:hover:not(:disabled) {
    background: var(--color-bg-hover, #777);
  }

  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }

  .loading-spinner {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 1s linear infinite;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
